import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Breadcrumb, ServiceProgressCard, Title } from './components';
import getProcessos from '../../services/processo';
import { cpfMask } from '../../utils/cpfFormat';

type FilterType = 'cpf' | 'processo' | null;

export type FilterObject = {
  param: FilterType;
  value: string;
};

export default function CheckProcess(): JSX.Element {
  const useStyles = makeStyles((theme: Theme) => ({
    main: {
      width: '100%',
      minHeight: 399,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: '24px',
    },
    box: {
      width: '100%',
      display: 'flex',
      maxWidth: '1440px',
      marginBottom: '40px',
      justifyContent: 'center',
    },
    contentFilter: {
      display: 'flex',
      width: '100%',
      maxWidth: 800,
      alignItems: 'center', // Alinhamento vertical centralizado
      justifyContent: 'center',
      gap: 20,
      margin: '0 auto',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        marginBottom: 20,
      },
    },
    formControlSelect: {
      marginTop: 20,
      marginBottom: 20,
      width: 250,
    },
    menuItem: {
      color: '#333',
    },
    formControl: {
      width: 250,
    },
    filter: {
      width: 250,
    },
    input: {
      color: '#333',
    },
    textPrimary: {
      marginTop: 20,
      color: '#424242',
      backgroundColor: '#fff',
      fontSize: 20,
      textAlign: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 200,
    },
    textSecondary: {
      color: '#757575',
      fontSize: 18,
      textAlign: 'center',
    },
    userInformation: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: '#fff',
    },
  }));
  const classes = useStyles();

  const [filter, setFilter] = useState<FilterType>(null);

  const [isSearch, setIsSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [searchData, setSearchData] = useState<string>('');
  const [processes, setProcesses] = useState([]);

  const validateCPF = (cpf: string): boolean => {
    const unmaskedCPF = cpf.replace(/\D/g, '');
    return unmaskedCPF.length === 11;
  };

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setSearchData('');
    setError(null);
  };

  const handleSearch = async () => {
    try {
      setError(null);
      setIsLoading(true);
      if (!searchData?.trim()) {
        setError('Deverá preencher pelo menos um campo.');
        return;
      }

      if (filter === 'cpf' && !validateCPF(searchData)) {
        setError('CPF inválido. Por favor, digite um CPF válido.');
        setIsLoading(false);
        return;
      }

      let filtro: FilterObject;
      switch (filter) {
        case 'cpf':
          filtro = {
            param: filter,
            value: searchData.replace(/\D/g, ''),
          };
          break;
        case 'processo':
          filtro = {
            param: filter,
            value: searchData,
          };
          break;
        default:
          setError('Selecione um tipo de filtro');
          return;
      }

      const { data } = await getProcessos(filtro);

      if (data?.processos?.length === 0) {
        setError('Nenhum processo encontrado');
      } else {
        setProcesses(data.processos);
        setIsSearch(true);
        setError(null);
      }
    } catch (err) {
      setError('Erro ao buscar processos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (filter === 'cpf') {
      setSearchData(cpfMask(value));
    } else {
      setSearchData(value);
    }
  };

  const renderFilterInput = () => {
    switch (filter) {
      case 'cpf':
        return (
          <TextField
            label="Digite o CPF do cidadão"
            variant="outlined"
            style={{ width: 250 }}
            value={searchData}
            onChange={handleChangeSearch}
            InputProps={{ className: classes.input, placeholder: '000.000.000-00' }}
          />
        );
      case 'processo':
        return (
          <TextField
            label="Digite o número do processo"
            variant="outlined"
            style={{ width: 250 }}
            value={searchData}
            onChange={handleChangeSearch}
            InputProps={{ className: classes.input }}
          />
        );
      default:
        return null;
    }
  };

  const buttonIsDisabled = !searchData?.length;

  const handleResetInputSearch = () => {
    setFilter(null);
    setSearchData('');
    setIsSearch(false);
  };

  return (
    <Box className={classes.main}>
      <Breadcrumb slug="Consultar Processo" />
      <Title />

      <Box padding="49px 10px">

        <Box className={classes.contentFilter}>
          <FormControl variant="outlined" className={classes.formControlSelect}>
            <InputLabel>Filtrar por</InputLabel>
            <Select
              className={classes.menuItem}
              value={filter || ''}
              onChange={(e) => handleFilterChange(e.target.value as FilterType)}
              label="Filtrar por"
            >
              <MenuItem className={classes.menuItem} value="">Filtrar por</MenuItem>
              <MenuItem className={classes.menuItem} value="cpf">CPF</MenuItem>
              <MenuItem className={classes.menuItem} value="processo">Número do Processo</MenuItem>
            </Select>
          </FormControl>

          {renderFilterInput()}

          {!isSearch ? (
            <Button
              variant="contained"
              color="primary"
              disabled={buttonIsDisabled || !filter}
              onClick={handleSearch}
            >
              Buscar
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleResetInputSearch}
            >
              Limpar
            </Button>
          )}
        </Box>

        {/* informações do processo */}

        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isSearch && processes.length > 0 && (
            <>
              <Box className={classes.userInformation}>
                <Typography className={classes.textPrimary}>
                  ACOMPANHAMENTO DA EMISSÃO CIN (CARTEIRA DE IDENTIDADE NACIONAL)
                </Typography>
                <Typography className={classes.textSecondary}>
                  Nº Processo:
                  {' '}
                  {processes[0].numero_processo}
                </Typography>
                <Typography className={classes.textSecondary}>
                  Nome do cidadão:
                  {' '}
                  {processes[0].nome_cidadao}
                </Typography>
                <Typography className={classes.textSecondary}>
                  CPF cidadão:
                  {' '}
                  {processes[0].cpf_cidadao}
                </Typography>
              </Box>

              <ServiceProgressCard
                statusProcesso={processes[0].status_processo}
              />
            </>
            )}
          </>
        )}

        {error && (
        <Typography color="error" style={{ marginTop: 10, textAlign: 'center' }}>
          {error}
        </Typography>
        )}

      </Box>
    </Box>
  );
}
